import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { AppContext } from '../../data/app-context';
import { SharedCache, SharedCacheOptions } from '../shared-cache';
import { GatewayInventoryDetail, GatewayInventoryDetailData, GatewayInventoryDetailParams } from './gateway-inventory-detail';

/**
 * Gateway Inventory Detail cache class.
 */
export class GatewayInventoryDetailCache
    extends SharedCache<GatewayInventoryDetail, GatewayInventoryDetailData, GatewayInventoryDetailParams> {
    private static uniqueId = '@msft-sme/shell:gatewayInventoryDetail';
    private static uniqueVersion = 1;
    private static gatewayName = 'gateway';

    /**
     * Initializes a new instance of the GatewayInventoryCache class.
     *
     * @param appContext the app context.
     * @param options the option of shared cache.
     */
    constructor(private appContext: AppContext, options?: SharedCacheOptions) {
        super(
            GatewayInventoryDetailCache.uniqueId,
            GatewayInventoryDetailCache.uniqueVersion,
            () => this.dataInstanceId(),
            (instance) => this.dataSerialize(instance),
            (serialized) => this.dataDeserialize(serialized),
            () => this.dataQuery(),
            options);
    }

    /**
     * Defines how to collect the gateway inventory detail data.
     *
     * @return the Observable of GatewayInventoryDetail data.
     */
    private dataQuery(): Observable<GatewayInventoryDetail> {
        return this.appContext.gateway.get('gateway/latestversion')
            .pipe(
                map((versionDetail: any) => {
                    const inventoryDetail = new GatewayInventoryDetail();

                    if (versionDetail) {
                        inventoryDetail.latestVersion =
                            versionDetail.destinationUrl.substring(versionDetail.destinationUrl.lastIndexOf('/') + 1);
                    } else {
                        inventoryDetail.latestVersion = null;
                    }

                    return inventoryDetail;
                }));
    }

    /**
     * Defines how to identify the cache entry by params.
     *
     * @return the id string.
     */
    private dataInstanceId(): string {
        return GatewayInventoryDetailCache.gatewayName;
    }

    /**
     * Defines how to deserialize the class object from serialized data.
     *
     * @param serialized the serialized string;
     */
    private dataDeserialize(serialized: string): GatewayInventoryDetail {
        const inventory: GatewayInventoryDetailData = JSON.parse(serialized);
        return new GatewayInventoryDetail(inventory);
    }

    /**
     * Defines how to serialize the class object to seralized data.
     *
     * @param instance the class instance.
     */
    private dataSerialize(instance: GatewayInventoryDetail): string {
        // automatically strip out class related data.
        return JSON.stringify(instance);
    }
}
